import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { useGetDashboardKpiQuery } from '../order/orders-api-slice';
import { selectCurrentUser } from '../user';
import {
  ManufacturerFilter,
  MondayWeekStartField,
  ReportLayout,
  ReportTablePagination,
  SortableHeadCell,
  useSortableTable,
} from './components';
import {
  WEEKLY_FEET_PER_DAY_X_AXIS_LABEL,
  WEEKLY_FEET_PER_DAY_Y_AXIS_LABEL,
  weeklyFeetPerDayBarChartMargin,
  weeklyFeetPerDayPlotBandSx,
  weeklyFeetPerDayYAxisWidth,
} from './report-chart-config';
import { getMostRecentMonday } from './report-utils';

interface WeeklyDayRow {
  day: string;
  Saw: number;
  Weld: number;
  Paint: number;
}

const WEEKDAY_ORDER: Record<string, number> = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
};

const WEEKLY_FEET_PER_DAY_SORT_ACCESSORS: Record<string, (row: WeeklyDayRow) => string | number> = {
  day: r => WEEKDAY_ORDER[r.day] ?? 99,
  Saw: r => r.Saw,
  Weld: r => r.Weld,
  Paint: r => r.Paint,
  total: r => r.Saw + r.Weld + r.Paint,
};

/**
 * UDOMI-504: Weekly Feet Per Day Report
 * Displays a grouped bar chart showing daily production (Saw, Weld, Paint) for a week (Monday-Friday).
 * Accessible to: Administrator, Supervisor, CSR
 * Administrators can select a manufacturer; others see their assigned manufacturer.
 */
export default function WeeklyFeetPerDayReport() {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);

  // Production stage colors per AC specification (Saw=yellow, Weld=orange, Paint=purple)
  const sawColor = '#ffcc00';
  const weldColor = '#ff6633';
  const paintColor = '#a600ff';

  // Determine user role
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;

  // State: table vs graph view toggle
  const [viewMode, setViewMode] = useState<'table' | 'graph'>('table');

  // State: selected Monday date (YYYY-MM-DD format)
  const [selectedMonday, setSelectedMonday] = useState<string>(
    getMostRecentMonday().toISODate() ?? ''
  );

  // State: selected manufacturer (admin only)
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    undefined
  );

  // Calculate start/end dates for the week
  const startDate = selectedMonday; // Monday
  const endDate = selectedMonday
    ? DateTime.fromISO(selectedMonday).plus({ days: 4 }).toISODate()
    : '';

  // Build request for Dashboard KPI — admin: undefined until manufacturer selected (query skipped);
  // non-admin: auto-scoped to their assigned manufacturer (BR-1).
  const kpiRequest = useMemo(() => {
    const manufacturerId = isAdmin ? selectedManufacturerId : currentUser?.manufacturerId;

    return {
      manufacturerId,
      viewMode: 'week' as const,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      includePartialPeriods: true,
    };
  }, [isAdmin, selectedManufacturerId, currentUser?.manufacturerId, startDate, endDate]);

  // Fetch Dashboard KPI data — skip until manufacturerId is resolved (admin must select one; BR-1)
  const {
    data: kpiData,
    isLoading,
    error,
  } = useGetDashboardKpiQuery(kpiRequest, {
    skip: !kpiRequest.manufacturerId,
  });

  // Transform daily breakdown for chart
  const chartData = useMemo<WeeklyDayRow[]>(() => {
    if (!kpiData?.productionMetricsByStage?.dailyBreakdown) {
      return [];
    }

    // UDOMI-907: Filter to Mon–Fri only. The backend returns all 7 days;
    // the report specification requires exactly 5 day groups.
    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    return kpiData.productionMetricsByStage.dailyBreakdown
      .filter(day => weekdays.includes(day.dayName))
      .map(day => ({
        day: day.dayName,
        Saw: day.sawFeet,
        Weld: day.weldFeet,
        Paint: day.paintFeet,
      }));
  }, [kpiData]);

  const {
    pageRows,
    sortedRows,
    page,
    rowsPerPage,
    isLastPage,
    getSortProps,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSortableTable<WeeklyDayRow>({
    data: chartData,
    accessors: WEEKLY_FEET_PER_DAY_SORT_ACCESSORS,
    initialSortField: 'day',
    initialSortDirection: 'asc',
  });

  return (
    <ReportLayout
      title="Weekly Feet Per Day Report"
      description="Daily production breakdown for the selected week (Monday-Friday)"
      isLoading={isLoading}
      error={error}
      filters={
        <>
          <ManufacturerFilter
            isAdmin={isAdmin}
            selectedManufacturerId={selectedManufacturerId}
            onManufacturerChange={setSelectedManufacturerId}
          />
          <MondayWeekStartField value={selectedMonday} onChange={setSelectedMonday} />
        </>
      }
    >
      {isAdmin && !selectedManufacturerId ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="body1" color="text.secondary">
            Please select a manufacturer to view this report.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Table / Graph toggle */}
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => {
                if (v) setViewMode(v);
              }}
              size="small"
            >
              <ToggleButton value="table">Table</ToggleButton>
              <ToggleButton value="graph">Graph</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Card — Table or Chart */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Week of {DateTime.fromISO(selectedMonday).toFormat('MMMM d, yyyy')}
              </Typography>
              {chartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <Typography variant="body1" color="text.secondary">
                    No production data available for this week
                  </Typography>
                </Box>
              ) : viewMode === 'table' ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <SortableHeadCell sx={{ fontWeight: 'bold' }} {...getSortProps('day')}>
                          Day
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold', color: sawColor }}
                          align="right"
                          {...getSortProps('Saw')}
                        >
                          Saw (ft)
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold', color: weldColor }}
                          align="right"
                          {...getSortProps('Weld')}
                        >
                          Weld (ft)
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold', color: paintColor }}
                          align="right"
                          {...getSortProps('Paint')}
                        >
                          Paint (ft)
                        </SortableHeadCell>
                        <SortableHeadCell
                          sx={{ fontWeight: 'bold' }}
                          align="right"
                          {...getSortProps('total')}
                        >
                          Total (ft)
                        </SortableHeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map(row => (
                        <TableRow key={row.day} hover>
                          <TableCell>{row.day}</TableCell>
                          <TableCell align="right">{row.Saw.toFixed(2)}</TableCell>
                          <TableCell align="right">{row.Weld.toFixed(2)}</TableCell>
                          <TableCell align="right">{row.Paint.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {(row.Saw + row.Weld + row.Paint).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row (full week, shown on the last page) */}
                      {isLastPage && (
                        <TableRow sx={{ bgcolor: 'grey.50', fontWeight: 'bold' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Totals</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: sawColor }}>
                            {sortedRows.reduce((s, r) => s + r.Saw, 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: weldColor }}>
                            {sortedRows.reduce((s, r) => s + r.Weld, 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: paintColor }}>
                            {sortedRows.reduce((s, r) => s + r.Paint, 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {sortedRows
                              .reduce((s, r) => s + r.Saw + r.Weld + r.Paint, 0)
                              .toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <ReportTablePagination
                    count={chartData.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              ) : (
                <Box>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData} margin={weeklyFeetPerDayBarChartMargin}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }}>
                        <Label
                          value={WEEKLY_FEET_PER_DAY_X_AXIS_LABEL}
                          position="bottom"
                          offset={12}
                        />
                      </XAxis>
                      <YAxis width={weeklyFeetPerDayYAxisWidth} tick={{ fontSize: 12 }}>
                        <Label
                          value={WEEKLY_FEET_PER_DAY_Y_AXIS_LABEL}
                          angle={-90}
                          position="insideLeft"
                          style={{ textAnchor: 'middle' }}
                        />
                      </YAxis>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                        formatter={(value: number, name: string) => [
                          `${value.toFixed(2)} ft`,
                          name,
                        ]}
                      />
                      <Bar dataKey="Saw" fill={sawColor} name="Saw" />
                      <Bar dataKey="Weld" fill={weldColor} name="Weld" />
                      <Bar dataKey="Paint" fill={paintColor} name="Paint" />
                    </BarChart>
                  </ResponsiveContainer>
                  {/* UDOMI-1034: center legend under Day of Week outside Recharts margin math */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 3,
                      pt: 1,
                      flexWrap: 'wrap',
                      ...weeklyFeetPerDayPlotBandSx,
                    }}
                  >
                    {[
                      { label: 'Saw', color: sawColor },
                      { label: 'Weld', color: weldColor },
                      { label: 'Paint', color: paintColor },
                    ].map(stage => (
                      <Box key={stage.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            bgcolor: stage.color,
                            borderRadius: 0.5,
                          }}
                        />
                        <Typography variant="body2" color="text.primary">
                          {stage.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Summary Card */}
          {kpiData?.productionMetricsByStage && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Week Total
                </Typography>
                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Saw
                    </Typography>
                    <Typography variant="h5" sx={{ color: sawColor }}>
                      {kpiData.productionMetricsByStage.sawFeet.toFixed(2)} ft
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Weld
                    </Typography>
                    <Typography variant="h5" sx={{ color: weldColor }}>
                      {kpiData.productionMetricsByStage.weldFeet.toFixed(2)} ft
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Paint
                    </Typography>
                    <Typography variant="h5" sx={{ color: paintColor }}>
                      {kpiData.productionMetricsByStage.paintFeet.toFixed(2)} ft
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </ReportLayout>
  );
}
